// src/components/routing/AssignmentRuleManagementComponent.js
// FlowSense AI — Enhanced Assignment Rules v2.0
// Glass Morphism, Framer Motion, Enhanced Table, Performance Optimized
// --- UPDATE (FlowSense v3 · AI Explainer)
//   • Coverage map: every zone × work-order type, showing the team an enabled rule routes it to,
//     or a gap. Click a gap to create the missing rule pre-filled. The "Coverage" stat now counts
//     covered combinations (was the number of possible combinations).
//   • FlowSense AI explainer: rule findings from the live rules / teams / zones (gaps — emergency
//     gaps first —, rules pointing at deleted teams or zones, disabled rules, priority ties) and an
//     AI explanation of how jobs will be routed.
//   • Bulk enable / disable, CSV export, team filter.
//   • Bug fixes: ids from <select> are strings, so the duplicate check and the preview never found
//     numeric zone / team ids; the bulk delete now reports partial failures instead of failing all.
// --- END UPDATE

import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
    Box, Button, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner, Alert,
    AlertIcon, HStack, useToast, Heading, Flex, IconButton, useDisclosure,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    FormControl, FormLabel, Input, Select, Switch, NumberInput, NumberInputField, VStack, Tag,
    Text, Badge, Tooltip, InputGroup, InputLeftElement, InputRightElement, useColorModeValue,
    Divider, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
    AlertDialogContent, AlertDialogOverlay, Stat, StatLabel, StatNumber, StatHelpText,
    SimpleGrid, Card, CardBody, Menu, MenuButton, MenuList, MenuItem, Checkbox,
    Icon, Skeleton, Progress, Collapse,
} from '@chakra-ui/react';
import {
    AddIcon, DeleteIcon, EditIcon, SearchIcon, InfoIcon, CheckCircleIcon,
    WarningIcon, CopyIcon, CloseIcon,
} from '@chakra-ui/icons';
import {
    MdRule, MdCheckCircle, MdWarning, MdRefresh, MdLayers, MdFilterList,
    MdBolt, MdTimeline, MdGridView, MdToggleOn, MdToggleOff,
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import * as assignmentRuleService from '../../services/assignmentRuleService';
import * as teamService from '../../services/teamService';
import * as assignmentZoneService from '../../services/assignmentZoneService';
// --- UPDATE (AI Explainer)
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer';
const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
const csvCell = (v) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// MOTION WRAPPERS & PRESETS
// ─────────────────────────────────────────────────────────────────────────────
const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr   = motion(Tr);

const ANIM = {
    fadeInUp:   { initial:{ opacity:0, y:16 },  animate:{ opacity:1, y:0 } },
    fadeInDown: { initial:{ opacity:0, y:-16 }, animate:{ opacity:1, y:0 } },
    scaleIn:    { initial:{ opacity:0, scale:0.92 }, animate:{ opacity:1, scale:1 } },
    stagger:    (i, base=0.05) => ({ delay: i * base }),
    spring:     { type:'spring', stiffness:300, damping:24 },
    smooth:     { duration:0.3, ease:[0.25, 0.46, 0.45, 0.94] },
};

const PulseIndicator = ({ color='green', size=2 }) => (
    <Box position="relative" display="inline-flex" w={`${size*4}px`} h={`${size*4}px`} flexShrink={0}>
        <MotionBox position="absolute" inset={0} borderRadius="full" bg={`${color}.400`}
            animate={{ scale:[1,1.8,1], opacity:[0.7,0,0.7] }}
            transition={{ duration:2, repeat:Infinity, ease:'easeInOut' }} />
        <Box position="absolute" inset={0} borderRadius="full" bg={`${color}.500`} />
    </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS (original)
// ─────────────────────────────────────────────────────────────────────────────
const WORK_ORDER_TYPES = ['standard', 'emergency', 'inspection', 'maintenance', 'repair', 'upgrade'];
const PRIORITY_RANGES = { low: [1, 50], medium: [51, 100], high: [101, 200], critical: [201, 999] };

const TYPE_COLORS = {
    standard:'blue', emergency:'red', inspection:'purple', maintenance:'teal', repair:'orange', upgrade:'green',
};
const getTypeColor = (type) => TYPE_COLORS[type] || 'gray';

const getPriorityColor = (priority) => {
    if (priority <= 50) return 'green';
    if (priority <= 100) return 'blue';
    if (priority <= 200) return 'orange';
    return 'red';
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARED SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const EnhancedStatCard = memo(({ label, value, helpText, icon, color='blue', isLoading:loading }) => {
    const cardBg      = useColorModeValue('white','gray.800');
    const bdrColor    = useColorModeValue('gray.200','gray.600');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    return (
        <MotionBox whileHover={{ y:-3, boxShadow:'0 8px 25px rgba(0,0,0,0.1)' }} transition={{ duration:0.2 }}>
            <Card bg={cardBg} borderWidth="1px" borderColor={bdrColor} borderRadius="xl"
                overflow="hidden" position="relative">
                <Box position="absolute" top={0} left={0} right={0} h="3px"
                    bgGradient={`linear(to-r, ${color}.400, ${color}.600)`} />
                <CardBody py={4} px={5}>
                    {loading ? (
                        <VStack align="start" spacing={2}>
                            <Skeleton h="12px" w="80px" borderRadius="md" />
                            <Skeleton h="28px" w="50px" borderRadius="md" />
                        </VStack>
                    ) : (
                        <HStack justify="space-between" align="start">
                            <Stat size="sm">
                                <StatLabel fontSize="xs" fontWeight="600" color={mutedColor}
                                    textTransform="uppercase" letterSpacing="wider">{label}</StatLabel>
                                <StatNumber fontSize="2xl" fontWeight="800" color={`${color}.500`}>{value}</StatNumber>
                                <StatHelpText fontSize="xs" mb={0}>{helpText}</StatHelpText>
                            </Stat>
                            {icon && <Box p={2} borderRadius="xl" bg={`${color}.50`}>
                                <Icon as={icon} boxSize={5} color={`${color}.500`} />
                            </Box>}
                        </HStack>
                    )}
                </CardBody>
            </Card>
        </MotionBox>
    );
});

const EmptyState = memo(({ icon:IconComp, title, subtitle }) => (
    <Flex direction="column" align="center" justify="center" py={10} gap={3}>
        <MotionBox animate={{ y:[0,-6,0] }} transition={{ duration:3, repeat:Infinity, ease:'easeInOut' }}>
            <Icon as={IconComp || MdRule} boxSize={14} color="gray.300" />
        </MotionBox>
        <Text fontWeight="700" color="gray.400">{title}</Text>
        {subtitle && <Text fontSize="sm" color="gray.400" textAlign="center" maxW="300px">{subtitle}</Text>}
    </Flex>
));

// ─────────────────────────────────────────────────────────────────────────────
// RULE TABLE ROW — memoized
// ─────────────────────────────────────────────────────────────────────────────
const RuleRow = memo(({ rule, index, isSelected, onSelect, onEdit, onDuplicate, onDelete, onToggle }) => {
    const hoverBg   = useColorModeValue('gray.50','gray.700');
    const mutedClr  = useColorModeValue('gray.500','gray.400');
    return (
        <MotionTr initial={{ opacity:0, x:-10 }} animate={{ opacity:1, x:0 }}
            exit={{ opacity:0, x:20 }}
            transition={{ delay:index*0.025, duration:0.25 }}
            _hover={{ bg:hoverBg }} style={{ transition:'background 0.15s' }}
            opacity={rule.is_enabled ? 1 : 0.6}>
            <Td>
                <Checkbox isChecked={isSelected} colorScheme="blue" size="sm"
                    onChange={e => onSelect(rule.id, e.target.checked)} />
            </Td>
            <Td>
                <Text fontWeight="700" fontSize="sm">{rule.rule_name}</Text>
            </Td>
            <Td>
                <Tag colorScheme="purple" borderRadius="full" size="sm" variant="subtle">{rule.zone_name}</Tag>
            </Td>
            <Td>
                <Tag colorScheme={getTypeColor(rule.work_order_type)} borderRadius="full" size="sm" variant="subtle">
                    {rule.work_order_type?.charAt(0).toUpperCase() + rule.work_order_type?.slice(1)}
                </Tag>
            </Td>
            <Td>
                <Text fontSize="sm" fontWeight="600">{rule.team_name}</Text>
            </Td>
            <Td>
                <Tooltip label="Lower priority runs first" hasArrow>
                    <Tag colorScheme={getPriorityColor(rule.priority)} borderRadius="full" size="sm"
                        fontWeight="700">{rule.priority}</Tag>
                </Tooltip>
            </Td>
            <Td>
                <Tooltip label="Click to toggle" hasArrow>
                    <Badge colorScheme={rule.is_enabled ? 'green' : 'gray'} cursor="pointer"
                        borderRadius="full" px={2.5} py={0.5} fontSize="2xs"
                        onClick={() => onToggle(rule)}
                        _hover={{ opacity:0.8, transform:'scale(1.05)' }} transition="all 0.15s">
                        <HStack spacing={1}>
                            <Icon as={rule.is_enabled ? MdToggleOn : MdToggleOff} boxSize={3} />
                            <Text>{rule.is_enabled ? 'Enabled' : 'Disabled'}</Text>
                        </HStack>
                    </Badge>
                </Tooltip>
            </Td>
            <Td>
                <HStack spacing={1}>
                    <Tooltip label="Edit Rule" hasArrow>
                        <IconButton icon={<EditIcon />} size="xs" aria-label="Edit" variant="ghost"
                            borderRadius="full" onClick={() => onEdit(rule)}
                            _hover={{ bg:'blue.50', color:'blue.600' }} />
                    </Tooltip>
                    <Tooltip label="Duplicate Rule" hasArrow>
                        <IconButton icon={<CopyIcon />} size="xs" aria-label="Duplicate" variant="ghost"
                            borderRadius="full" onClick={() => onDuplicate(rule)}
                            _hover={{ bg:'purple.50', color:'purple.600' }} />
                    </Tooltip>
                    <Tooltip label="Delete Rule" hasArrow>
                        <IconButton icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost"
                            aria-label="Delete" borderRadius="full" onClick={() => onDelete(rule)}
                            _hover={{ bg:'red.50' }} />
                    </Tooltip>
                </HStack>
            </Td>
        </MotionTr>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const AssignmentRuleManagementComponent = () => {
    // ── State (all original) ─────────────────────────────────────────────────
    const [rules, setRules] = useState([]);
    const [teams, setTeams] = useState([]);
    const [zones, setZones] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterEnabled, setFilterEnabled] = useState(null);
    const [filterType, setFilterType] = useState('');
    const [sortBy, setSortBy] = useState('priority');
    const [sortOrder, setSortOrder] = useState('asc');
    const [selectedRules, setSelectedRules] = useState([]);
    const [ruleToDelete, setRuleToDelete] = useState(null);
    const [filterTeam, setFilterTeam] = useState(''); // --- UPDATE
    const [showCoverage, setShowCoverage] = useState(true); // --- UPDATE

    const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isBulkDeleteOpen, onOpen: onBulkDeleteOpen, onClose: onBulkDeleteClose } = useDisclosure();
    const cancelRef = React.useRef();
    const toast = useToast();

    const [currentRule, setCurrentRule] = useState(null);
    const [formData, setFormData] = useState({});
    const [formErrors, setFormErrors] = useState({});

    // ── Theme tokens ─────────────────────────────────────────────────────────
    const bgColor        = useColorModeValue('white', 'gray.800');
    const borderColor    = useColorModeValue('gray.200', 'gray.600');
    const mutedColor     = useColorModeValue('gray.500', 'gray.400');
    const searchBg       = useColorModeValue('white', 'gray.700');
    const headerGradient = useColorModeValue(
        'linear(to-r, purple.500, blue.500)',
        'linear(to-r, purple.400, blue.400)'
    );
    const previewBg      = useColorModeValue('blue.50', 'blue.900');

    // ── Data Fetching (original logic preserved) ─────────────────────────────
    const fetchData = useCallback(async () => {
        setIsLoading(true); setError(null);
        try {
            const [rulesData, teamsData, zonesData] = await Promise.all([
                assignmentRuleService.fetchAssignmentRules(),
                teamService.fetchTeams(),
                assignmentZoneService.fetchAssignmentZones(),
            ]);
            setRules(Array.isArray(rulesData) ? rulesData : []);
            setTeams(Array.isArray(teamsData) ? teamsData : []);
            setZones(Array.isArray(zonesData) ? zonesData : []);
        } catch (err) {
            const errorMessage = err?.message || 'Failed to fetch data for assignment rules engine.';
            setError(errorMessage);
            toast({ title:'Error fetching data', description:errorMessage, status:'error', duration:5000, isClosable:true });
        } finally { setIsLoading(false); }
    }, [toast]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Derived data (original logic preserved) ──────────────────────────────
    const filteredAndSortedRules = useMemo(() => {
        let filtered = rules.filter(rule => {
            const matchesSearch = !searchTerm ||
                rule.rule_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                rule.zone_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                rule.team_name?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesEnabled = filterEnabled === null || rule.is_enabled === filterEnabled;
            const matchesType = !filterType || rule.work_order_type === filterType;
            const matchesTeam = !filterTeam || sameId(rule.assigned_team_id, filterTeam); // --- UPDATE
            return matchesSearch && matchesEnabled && matchesType && matchesTeam;
        });
        filtered.sort((a, b) => {
            let aVal = a[sortBy]; let bVal = b[sortBy];
            if (sortBy === 'priority') { aVal = Number(aVal) || 0; bVal = Number(bVal) || 0; }
            else if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal?.toLowerCase() || ''; }
            return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
        });
        return filtered;
    }, [rules, searchTerm, filterEnabled, filterType, filterTeam, sortBy, sortOrder]);

    const ruleStats = useMemo(() => ({
        total: rules.length,
        enabled: rules.filter(r => r.is_enabled).length,
        disabled: rules.filter(r => !r.is_enabled).length,
        byType: WORK_ORDER_TYPES.reduce((acc, type) => { acc[type] = rules.filter(r => r.work_order_type === type).length; return acc; }, {}),
    }), [rules]);

    // --- UPDATE: coverage map + explainer findings (all from the loaded rules / teams / zones)
    const coverage = useMemo(() => {
        const teamName = (id) => teams.find(t => sameId(t.id, id))?.name || null;
        const cells = {};
        let covered = 0;
        zones.forEach(z => {
            WORK_ORDER_TYPES.forEach(type => {
                const active = rules
                    .filter(r => r.is_enabled && sameId(r.zone_id, z.id) && r.work_order_type === type)
                    .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0));
                const key = `${z.id}|${type}`;
                if (active.length) covered += 1;
                cells[key] = active.length ? { rule: active[0], team: teamName(active[0].assigned_team_id) || active[0].team_name || '?' } : null;
            });
        });
        return { cells, covered, possible: zones.length * WORK_ORDER_TYPES.length };
    }, [rules, teams, zones]);

    const explainer = useMemo(() => {
        const findings = [];
        const teamIds = new Set(teams.map(t => String(t.id)));
        const zoneIds = new Set(zones.map(z => String(z.id)));
        const orphanTeam = rules.filter(r => r.assigned_team_id != null && !teamIds.has(String(r.assigned_team_id)));
        const orphanZone = rules.filter(r => r.zone_id != null && !zoneIds.has(String(r.zone_id)));
        const emergencyGaps = zones.filter(z => !coverage.cells[`${z.id}|emergency`]).map(z => z.name);
        const zonesNoRule = zones.filter(z => !rules.some(r => r.is_enabled && sameId(r.zone_id, z.id))).map(z => z.name);
        const teamsNoRule = teams.filter(t => !rules.some(r => r.is_enabled && sameId(r.assigned_team_id, t.id))).map(t => t.name);
        const gaps = coverage.possible - coverage.covered;
        if (orphanTeam.length) findings.push({ severity: 'high', text: `${orphanTeam.length} rule(s) send work to a team that no longer exists.`, evidence: orphanTeam.slice(0, 4).map(r => r.rule_name).join(', ') });
        if (orphanZone.length) findings.push({ severity: 'high', text: `${orphanZone.length} rule(s) point at a zone that no longer exists.`, evidence: orphanZone.slice(0, 4).map(r => r.rule_name).join(', ') });
        if (zones.length && emergencyGaps.length) findings.push({ severity: 'high', text: `Emergency work has no enabled rule in ${emergencyGaps.length} of ${zones.length} zone(s) — it falls back to manual or AI dispatch.`, evidence: emergencyGaps.slice(0, 5).join(', ') });
        if (zonesNoRule.length) findings.push({ severity: 'medium', text: `${zonesNoRule.length} zone(s) have no enabled rule at all.`, evidence: zonesNoRule.slice(0, 5).join(', ') });
        if (coverage.possible) findings.push({ severity: gaps === 0 ? 'ok' : 'info', text: `${coverage.covered} of ${coverage.possible} zone × type combinations are covered by an enabled rule (${Math.round((coverage.covered / coverage.possible) * 100)} %).` });
        if (ruleStats.disabled) findings.push({ severity: 'info', text: `${ruleStats.disabled} rule(s) are disabled and are skipped by the assignment engine.` });
        if (teamsNoRule.length && teams.length) findings.push({ severity: 'info', text: `${teamsNoRule.length} team(s) receive no rule-based work.`, evidence: teamsNoRule.slice(0, 5).join(', ') });
        const tieKeys = {};
        rules.filter(r => r.is_enabled).forEach(r => { const k = `${r.zone_id}|${r.priority}`; tieKeys[k] = (tieKeys[k] || 0) + 1; });
        const ties = Object.values(tieKeys).filter(n => n > 1).length;
        if (ties) findings.push({ severity: 'medium', text: `${ties} zone(s) have enabled rules with the same priority — the order between them is not defined.` });
        if (!zones.length && !isLoading) findings.push({ severity: 'medium', text: 'No assignment zones are defined, so no rule can match.' });
        return {
            findings,
            facts: {
                rules_total: ruleStats.total, rules_enabled: ruleStats.enabled, rules_disabled: ruleStats.disabled,
                zones: zones.length, teams: teams.length,
                coverage: { covered: coverage.covered, possible: coverage.possible },
                emergency_gap_zones: emergencyGaps.slice(0, 10), zones_without_rules: zonesNoRule.slice(0, 10), teams_without_rules: teamsNoRule.slice(0, 10),
                rules: rules.slice(0, 25).map(r => ({ name: r.rule_name, zone: r.zone_name, type: r.work_order_type, team: r.team_name, priority: r.priority, enabled: !!r.is_enabled })),
                how_it_works: 'For a new work order, enabled rules for its zone and type are tried in ascending priority; the first match assigns the team. Without a match, AI dispatch or manual assignment is used.',
            },
        };
    }, [rules, teams, zones, coverage, ruleStats, isLoading]);

    const handleOpenGap = useCallback((zoneId, type) => {
        const z = zones.find(x => sameId(x.id, zoneId));
        setCurrentRule(null);
        setFormData({ rule_name: `${z?.name || 'Zone'} — ${type}`, zone_id: zoneId, work_order_type: type, assigned_team_id: '', priority: 100, is_enabled: true });
        setFormErrors({});
        onFormOpen();
    }, [zones, onFormOpen]);
    // --- END UPDATE

    // ── Handlers (all original logic preserved) ──────────────────────────────
    const validateForm = useCallback(() => {
        const errors = {};
        if (!formData.rule_name?.trim()) errors.rule_name = 'Rule name is required';
        else if (formData.rule_name.length > 255) errors.rule_name = 'Rule name must be less than 255 characters';
        if (!formData.zone_id) errors.zone_id = 'Zone is required';
        if (!formData.work_order_type) errors.work_order_type = 'Work order type is required';
        if (!formData.assigned_team_id) errors.assigned_team_id = 'Assigned team is required';
        if (formData.priority < 1 || formData.priority > 999) errors.priority = 'Priority must be between 1 and 999';
        const dup = rules.find(r => sameId(r.zone_id, formData.zone_id) && r.work_order_type === formData.work_order_type && !sameId(r.id, currentRule?.id)); // --- UPDATE: string/number safe
        if (dup) errors.zone_id = 'A rule for this zone and work order type already exists';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData, rules, currentRule]);

    const handleOpenModal = useCallback((rule = null) => {
        setCurrentRule(rule);
        setFormData({
            rule_name: rule?.rule_name || '', zone_id: rule?.zone_id || '',
            work_order_type: rule?.work_order_type || WORK_ORDER_TYPES[0],
            assigned_team_id: rule?.assigned_team_id || '',
            priority: rule?.priority || 100, is_enabled: rule?.is_enabled ?? true,
        });
        setFormErrors({});
        onFormOpen();
    }, [onFormOpen]);

    const handleDuplicateRule = useCallback((rule) => {
        setCurrentRule(null);
        setFormData({
            rule_name: `${rule.rule_name} (Copy)`, zone_id: rule.zone_id,
            work_order_type: rule.work_order_type, assigned_team_id: rule.assigned_team_id,
            priority: rule.priority + 1, is_enabled: true,
        });
        setFormErrors({});
        onFormOpen();
    }, [onFormOpen]);

    const handleFormChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
    }, [formErrors]);

    const handleFormSubmit = useCallback(async () => {
        if (!validateForm()) { toast({ title:'Validation Error', description:'Please fix the errors', status:'error', duration:3000, isClosable:true }); return; }
        setIsLoading(true);
        try {
            const payload = { ...formData };
            if (currentRule) {
                await assignmentRuleService.updateAssignmentRule(currentRule.id, payload);
                toast({ title:'Rule updated successfully', description:`"${payload.rule_name}" updated`, status:'success', duration:3000, isClosable:true });
            } else {
                await assignmentRuleService.createAssignmentRule(payload);
                toast({ title:'Rule created successfully', description:`"${payload.rule_name}" created`, status:'success', duration:3000, isClosable:true });
            }
            fetchData(); onFormClose();
        } catch (error) {
            toast({ title:'Error saving rule', description:error?.response?.data?.message || error?.message || 'Unknown error', status:'error', duration:5000, isClosable:true });
        } finally { setIsLoading(false); }
    }, [validateForm, formData, currentRule, fetchData, onFormClose, toast]);

    const handleDeleteRule = useCallback(async (ruleId) => {
        try {
            await assignmentRuleService.deleteAssignmentRule(ruleId);
            toast({ title:'Rule deleted successfully', status:'success', duration:3000, isClosable:true });
            fetchData(); onDeleteClose();
        } catch (err) {
            toast({ title:'Error deleting rule', description:err?.message || 'Failed to delete rule', status:'error', duration:5000, isClosable:true });
        }
    }, [fetchData, onDeleteClose, toast]);

    const handleBulkDelete = useCallback(async () => {
        setIsLoading(true);
        try {
            // --- UPDATE: allSettled — one failure no longer hides the ones that worked
            const results = await Promise.allSettled(selectedRules.map(id => assignmentRuleService.deleteAssignmentRule(id)));
            const ok = results.filter(r => r.status === 'fulfilled').length;
            const failed = results.length - ok;
            toast({ title: failed ? `${ok} deleted, ${failed} failed` : `${ok} rule(s) deleted`, status: failed ? 'warning' : 'success', duration:4000, isClosable:true });
            setSelectedRules([]); fetchData(); onBulkDeleteClose();
        } catch (err) {
            toast({ title:'Error deleting rules', description:err?.message, status:'error', duration:5000, isClosable:true });
        } finally { setIsLoading(false); }
    }, [selectedRules, fetchData, onBulkDeleteClose, toast]);

    // --- UPDATE: bulk enable / disable + CSV export
    const handleBulkSetEnabled = useCallback(async (enabled) => {
        const targets = rules.filter(r => selectedRules.some(id => sameId(id, r.id)) && !!r.is_enabled !== enabled);
        if (!targets.length) { toast({ title: `Selected rules are already ${enabled ? 'enabled' : 'disabled'}`, status: 'info', duration: 2500 }); return; }
        setIsLoading(true);
        try {
            const results = await Promise.allSettled(targets.map(r => assignmentRuleService.updateAssignmentRule(r.id, { ...r, is_enabled: enabled })));
            const ok = results.filter(x => x.status === 'fulfilled').length;
            toast({ title: `${ok} rule(s) ${enabled ? 'enabled' : 'disabled'}${ok < targets.length ? `, ${targets.length - ok} failed` : ''}`, status: ok < targets.length ? 'warning' : 'success', duration: 3000, isClosable: true });
            fetchData();
        } finally { setIsLoading(false); }
    }, [rules, selectedRules, fetchData, toast]);

    const handleExportCsv = useCallback(() => {
        const head = ['rule_name', 'zone', 'work_order_type', 'team', 'priority', 'enabled'];
        const lines = [head.join(',')].concat(filteredAndSortedRules.map(r => [r.rule_name, r.zone_name, r.work_order_type, r.team_name, r.priority, r.is_enabled ? 'yes' : 'no'].map(csvCell).join(',')));
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `assignment-rules-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, [filteredAndSortedRules]);
    // --- END UPDATE

    const handleToggleEnabled = useCallback(async (rule) => {
        try {
            await assignmentRuleService.updateAssignmentRule(rule.id, { ...rule, is_enabled: !rule.is_enabled });
            toast({ title:`Rule ${!rule.is_enabled ? 'enabled' : 'disabled'}`, status:'success', duration:2000, isClosable:true });
            fetchData();
        } catch (err) {
            toast({ title:'Error updating rule', description:err?.message, status:'error', duration:3000, isClosable:true });
        }
    }, [fetchData, toast]);

    const handleSelectAll = useCallback(() => {
        setSelectedRules(prev =>
            prev.length === filteredAndSortedRules.length ? [] : filteredAndSortedRules.map(r => r.id)
        );
    }, [filteredAndSortedRules]);

    const handleRuleSelect = useCallback((id, checked) => {
        setSelectedRules(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
    }, []);

    const initiateDelete = useCallback((rule) => { setRuleToDelete(rule); onDeleteOpen(); }, [onDeleteOpen]);

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Box>
            {/* ═══════ HEADER ═══════ */}
            <MotionFlex {...ANIM.fadeInDown} transition={ANIM.smooth}
                justifyContent="space-between" alignItems="center" mb={6}>
                <HStack spacing={4}>
                    <MotionBox initial={{ scale:0, rotate:-180 }} animate={{ scale:1, rotate:0 }}
                        transition={{ type:'spring', stiffness:200, damping:15 }}>
                        <Box bgGradient={headerGradient} p={3} borderRadius="xl"
                            boxShadow="0 4px 14px rgba(128,90,213,0.3)">
                            <Icon as={MdRule} color="white" boxSize={6} />
                        </Box>
                    </MotionBox>
                    <Box>
                        <Heading size="md" fontWeight="800">Assignment Rules Management</Heading>
                        <HStack spacing={2} mt={0.5}>
                            <Text color={mutedColor} fontSize="sm">Automatic work order assignment based on zones and types</Text>
                            <PulseIndicator color="purple" size={1.5} />
                        </HStack>
                    </Box>
                </HStack>
                <HStack spacing={2}>
                    <Tooltip label="Refresh" hasArrow>
                        <IconButton icon={<Icon as={MdRefresh} />} variant="ghost" borderRadius="full"
                            onClick={fetchData} isLoading={isLoading} aria-label="Refresh"
                            _hover={{ transform:'rotate(180deg)' }} transition="all 0.3s" />
                    </Tooltip>
                    <MotionBox whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}>
                        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={() => handleOpenModal()}
                            borderRadius="lg" boxShadow="0 4px 12px rgba(66,99,235,0.25)"
                            _hover={{ boxShadow:'0 6px 18px rgba(66,99,235,0.35)', transform:'translateY(-1px)' }}
                            transition="all 0.2s">
                            Create New Rule
                        </Button>
                    </MotionBox>
                </HStack>
            </MotionFlex>

            {/* ═══════ STAT CARDS ═══════ */}
            <SimpleGrid columns={{ base:1, md:4 }} spacing={4} mb={6}>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(0), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Total Rules" value={ruleStats.total}
                        helpText={<><Icon as={MdCheckCircle} color="green.500" mr={1} boxSize={3} />{ruleStats.enabled} active</>}
                        icon={MdRule} color="blue" isLoading={isLoading && rules.length === 0} />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(1), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Enabled" value={ruleStats.enabled}
                        helpText="Currently active rules" icon={MdCheckCircle} color="green"
                        isLoading={isLoading && rules.length === 0} />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(2), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Disabled" value={ruleStats.disabled}
                        helpText="Inactive rules" icon={MdWarning} color="gray"
                        isLoading={isLoading && rules.length === 0} />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(3), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Coverage" value={coverage.possible ? `${coverage.covered}/${coverage.possible}` : '—'}
                        helpText="Zone × type combinations with an enabled rule" icon={MdGridView} color="purple" /* --- UPDATE: was possible combinations only */
                        isLoading={isLoading && rules.length === 0} />
                </MotionBox>
            </SimpleGrid>

            {/* ═══════ UPDATE: AI EXPLAINER + COVERAGE MAP ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(4), ...ANIM.smooth }} mb={6}>
                <FlowSenseAIExplainer
                    module="assignment_rules"
                    title="How will new work be routed?"
                    subtitle="Gaps, conflicts and stale rules from the live rule set — and an AI explanation"
                    facts={explainer.facts}
                    findings={explainer.findings}
                    isLoading={isLoading && rules.length === 0}
                    defaultOpen
                    defaultQuestion="Explain in plain language how new work orders will be routed with these rules, and what I should fix first."
                    questions={['What should I fix first?', 'Where will emergency work go?', 'Which teams are overloaded or unused by rules?']}
                />
            </MotionBox>

            {zones.length > 0 && (
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(4), ...ANIM.smooth }} mb={6}>
                    <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" borderRadius="xl" boxShadow="sm">
                        <CardBody py={4}>
                            <Flex justify="space-between" align="center" mb={showCoverage ? 3 : 0} cursor="pointer" onClick={() => setShowCoverage(s => !s)}>
                                <HStack spacing={2}>
                                    <Icon as={MdLayers} color="purple.400" />
                                    <Text fontWeight="700" fontSize="sm">Coverage map</Text>
                                    <Badge colorScheme={coverage.covered === coverage.possible ? 'green' : 'orange'} borderRadius="full">
                                        {coverage.possible - coverage.covered} gap{coverage.possible - coverage.covered === 1 ? '' : 's'}
                                    </Badge>
                                </HStack>
                                <Text fontSize="xs" color={mutedColor}>{showCoverage ? 'Hide' : 'Show'} · click a gap to add a rule</Text>
                            </Flex>
                            <Collapse in={showCoverage} animateOpacity>
                                <TableContainer maxH="360px" overflowY="auto">
                                    <Table size="sm" variant="simple">
                                        <Thead position="sticky" top={0} bg={bgColor} zIndex={1}>
                                            <Tr>
                                                <Th>Zone</Th>
                                                {WORK_ORDER_TYPES.map(t => <Th key={t} textAlign="center">{t}</Th>)}
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {zones.map(z => (
                                                <Tr key={z.id}>
                                                    <Td fontWeight="600" fontSize="sm">{z.name}</Td>
                                                    {WORK_ORDER_TYPES.map(t => {
                                                        const c = coverage.cells[`${z.id}|${t}`];
                                                        return (
                                                            <Td key={t} textAlign="center" p={1}>
                                                                {c ? (
                                                                    <Tooltip label={`${c.rule.rule_name} · priority ${c.rule.priority}`} hasArrow>
                                                                        <Tag size="sm" colorScheme={getTypeColor(t)} variant="subtle" borderRadius="full" cursor="pointer" onClick={() => handleOpenModal(c.rule)} maxW="140px">
                                                                            <Text noOfLines={1}>{c.team}</Text>
                                                                        </Tag>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Tooltip label={`No enabled ${t} rule in ${z.name} — click to add`} hasArrow>
                                                                        <Button size="xs" variant="ghost" colorScheme={t === 'emergency' ? 'red' : 'gray'} borderRadius="full" onClick={() => handleOpenGap(z.id, t)}>
                                                                            {t === 'emergency' ? 'gap!' : '+'}
                                                                        </Button>
                                                                    </Tooltip>
                                                                )}
                                                            </Td>
                                                        );
                                                    })}
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            </Collapse>
                        </CardBody>
                    </Card>
                </MotionBox>
            )}
            {/* ═══════ END UPDATE ═══════ */}

            {/* ═══════ SEARCH / FILTER BAR ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(4), ...ANIM.smooth }}>
                <Card mb={6} bg={bgColor} borderColor={borderColor} borderWidth="1px" borderRadius="xl" boxShadow="sm">
                    <CardBody py={4}>
                        <VStack spacing={3} align="stretch">
                            <Flex gap={3} wrap="wrap" align="center">
                                <InputGroup maxW="280px" size="sm">
                                    <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                                    <Input placeholder="Search rules…" value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)} bg={searchBg}
                                        borderRadius="lg" fontSize="sm"
                                        _focus={{ borderColor:'purple.400', boxShadow:'0 0 0 1px var(--chakra-colors-purple-400)' }} />
                                    {searchTerm && (
                                        <InputRightElement cursor="pointer" onClick={() => setSearchTerm('')}>
                                            <CloseIcon color="gray.400" boxSize={2} />
                                        </InputRightElement>
                                    )}
                                </InputGroup>
                                <Select placeholder="All Types" maxW="160px" size="sm" borderRadius="lg"
                                    value={filterType} onChange={e => setFilterType(e.target.value)}>
                                    {WORK_ORDER_TYPES.map(type => (
                                        <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                                    ))}
                                </Select>
                                <Select placeholder="All Status" maxW="150px" size="sm" borderRadius="lg"
                                    value={filterEnabled === null ? '' : filterEnabled.toString()}
                                    onChange={e => setFilterEnabled(e.target.value === '' ? null : e.target.value === 'true')}>
                                    <option value="true">Enabled</option>
                                    <option value="false">Disabled</option>
                                </Select>
                                {/* --- UPDATE: team filter */}
                                <Select placeholder="All Teams" maxW="170px" size="sm" borderRadius="lg"
                                    value={filterTeam} onChange={e => setFilterTeam(e.target.value)}>
                                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </Select>
                                <Select maxW="150px" size="sm" borderRadius="lg" value={sortBy}
                                    onChange={e => setSortBy(e.target.value)}>
                                    <option value="priority">Priority</option>
                                    <option value="rule_name">Name</option>
                                    <option value="zone_name">Zone</option>
                                    <option value="work_order_type">Type</option>
                                </Select>
                                <Button onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                                    variant="outline" size="sm" borderRadius="lg" minW="36px">
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                </Button>
                                <AnimatePresence>
                                    {selectedRules.length > 0 && (
                                        <MotionBox initial={{ opacity:0, scale:0.8 }} animate={{ opacity:1, scale:1 }}
                                            exit={{ opacity:0, scale:0.8 }} transition={ANIM.spring}>
                                            <HStack spacing={2}>
                                                {/* --- UPDATE: bulk enable / disable */}
                                                <Button size="sm" variant="outline" colorScheme="green" borderRadius="lg" leftIcon={<Icon as={MdToggleOn} />} onClick={() => handleBulkSetEnabled(true)}>Enable</Button>
                                                <Button size="sm" variant="outline" borderRadius="lg" leftIcon={<Icon as={MdToggleOff} />} onClick={() => handleBulkSetEnabled(false)}>Disable</Button>
                                                <Button colorScheme="red" variant="outline" size="sm" borderRadius="lg"
                                                    onClick={onBulkDeleteOpen} leftIcon={<DeleteIcon />}>
                                                    Delete ({selectedRules.length})
                                                </Button>
                                            </HStack>
                                        </MotionBox>
                                    )}
                                </AnimatePresence>
                            </Flex>
                            <HStack justify="space-between">
                                <Badge colorScheme="purple" borderRadius="full" px={3} py={0.5} fontSize="2xs" w="fit-content">
                                    {filteredAndSortedRules.length} of {rules.length} rules
                                </Badge>
                                {/* --- UPDATE: CSV export */}
                                <Button size="xs" variant="ghost" onClick={handleExportCsv} isDisabled={!filteredAndSortedRules.length}>Export CSV</Button>
                            </HStack>
                        </VStack>
                    </CardBody>
                </Card>
            </MotionBox>

            {/* ═══════ DATA TABLE ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(5), ...ANIM.smooth }}>
                <TableContainer bg={bgColor} borderRadius="xl" borderWidth="1px" borderColor={borderColor}
                    boxShadow="sm" overflow="hidden">
                    {isLoading && <Progress size="xs" isIndeterminate colorScheme="purple" />}
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th w="40px">
                                    <Checkbox size="sm" colorScheme="blue"
                                        isChecked={selectedRules.length === filteredAndSortedRules.length && filteredAndSortedRules.length > 0}
                                        onChange={handleSelectAll} />
                                </Th>
                                <Th>Rule Name</Th><Th>Zone</Th><Th>Type</Th>
                                <Th>Team</Th><Th>Priority</Th><Th>Status</Th><Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {isLoading && rules.length === 0 && Array.from({ length:4 }).map((_, i) => (
                                <Tr key={i}>{Array.from({ length:8 }).map((_, j) => (
                                    <Td key={j}><Skeleton h="14px" w={j===0?'20px':'80px'} borderRadius="md" /></Td>
                                ))}</Tr>
                            ))}
                            {error && !isLoading && (
                                <Tr><Td colSpan={8}>
                                    <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>
                                </Td></Tr>
                            )}
                            {!isLoading && !error && filteredAndSortedRules.length === 0 && (
                                <Tr><Td colSpan={8}>
                                    <EmptyState icon={MdRule} title="No rules found"
                                        subtitle="Create a new assignment rule to get started" />
                                </Td></Tr>
                            )}
                            <AnimatePresence>
                                {!isLoading && filteredAndSortedRules.map((rule, idx) => (
                                    <RuleRow key={rule.id} rule={rule} index={idx}
                                        isSelected={selectedRules.some(id => sameId(id, rule.id))}
                                        onSelect={handleRuleSelect} onEdit={handleOpenModal}
                                        onDuplicate={handleDuplicateRule} onDelete={initiateDelete}
                                        onToggle={handleToggleEnabled} />
                                ))}
                            </AnimatePresence>
                        </Tbody>
                    </Table>
                </TableContainer>
            </MotionBox>

            {/* ═══════ FORM MODAL ═══════ */}
            <Modal isOpen={isFormOpen} onClose={onFormClose} size="xl" motionPreset="slideInBottom">
                <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
                <ModalContent borderRadius="2xl" overflow="hidden">
                    <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient={headerGradient} />
                    <ModalHeader pt={5} fontWeight="800">
                        {currentRule ? 'Edit Assignment Rule' : 'Create Assignment Rule'}
                    </ModalHeader>
                    <ModalCloseButton borderRadius="full" />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired isInvalid={!!formErrors.rule_name}>
                                <FormLabel fontSize="sm" fontWeight="600">Rule Name</FormLabel>
                                <Input name="rule_name" value={formData.rule_name} onChange={handleFormChange}
                                    placeholder="e.g., Emergency North Zone Rule" borderRadius="lg" />
                                {formErrors.rule_name && <Text color="red.500" fontSize="xs" mt={1}>{formErrors.rule_name}</Text>}
                            </FormControl>
                            <FormControl isRequired isInvalid={!!formErrors.zone_id}>
                                <FormLabel fontSize="sm" fontWeight="600">Zone</FormLabel>
                                <Select name="zone_id" placeholder="Select Zone" value={formData.zone_id}
                                    onChange={handleFormChange} borderRadius="lg">
                                    {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                                </Select>
                                {formErrors.zone_id && <Text color="red.500" fontSize="xs" mt={1}>{formErrors.zone_id}</Text>}
                            </FormControl>
                            <FormControl isRequired isInvalid={!!formErrors.work_order_type}>
                                <FormLabel fontSize="sm" fontWeight="600">Work Order Type</FormLabel>
                                <Select name="work_order_type" value={formData.work_order_type}
                                    onChange={handleFormChange} borderRadius="lg">
                                    {WORK_ORDER_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                                </Select>
                                {formErrors.work_order_type && <Text color="red.500" fontSize="xs" mt={1}>{formErrors.work_order_type}</Text>}
                            </FormControl>
                            <FormControl isRequired isInvalid={!!formErrors.assigned_team_id}>
                                <FormLabel fontSize="sm" fontWeight="600">Assigned Team</FormLabel>
                                <Select name="assigned_team_id" placeholder="Select Team" value={formData.assigned_team_id}
                                    onChange={handleFormChange} borderRadius="lg">
                                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}{t.team_lead_username ? ` (Lead: ${t.team_lead_username})` : ''}</option>)}
                                </Select>
                                {formErrors.assigned_team_id && <Text color="red.500" fontSize="xs" mt={1}>{formErrors.assigned_team_id}</Text>}
                            </FormControl>
                            <FormControl isInvalid={!!formErrors.priority}>
                                <FormLabel fontSize="sm" fontWeight="600">
                                    Priority
                                    <Tooltip label="Lower numbers run first. Range: 1-999" hasArrow>
                                        <InfoIcon ml={2} w={3} h={3} color={mutedColor} />
                                    </Tooltip>
                                </FormLabel>
                                <NumberInput value={formData.priority}
                                    onChange={(_, val) => setFormData(prev => ({ ...prev, priority: val || 100 }))}
                                    min={1} max={999}>
                                    <NumberInputField borderRadius="lg" />
                                </NumberInput>
                                {formErrors.priority && <Text color="red.500" fontSize="xs" mt={1}>{formErrors.priority}</Text>}
                            </FormControl>
                            <FormControl display="flex" alignItems="center">
                                <FormLabel htmlFor="is_enabled" mb="0" fontSize="sm" fontWeight="600">Enable Rule?</FormLabel>
                                <Switch id="is_enabled" name="is_enabled" isChecked={formData.is_enabled}
                                    onChange={handleFormChange} colorScheme="green" />
                            </FormControl>
                            <Divider />
                            <Box w="100%" p={3} bg={previewBg} borderRadius="xl" borderWidth="1px" borderColor="blue.200">
                                <HStack spacing={2} mb={2}>
                                    <Icon as={MdBolt} color="blue.500" boxSize={4} />
                                    <Text fontSize="sm" fontWeight="700">Rule Preview</Text>
                                </HStack>
                                <Text fontSize="sm">
                                    When a <Tag size="sm" colorScheme={getTypeColor(formData.work_order_type)} borderRadius="full">{formData.work_order_type}</Tag> work order
                                    is created in zone <Tag size="sm" colorScheme="purple" borderRadius="full">{zones.find(z => sameId(z.id, formData.zone_id))?.name || 'N/A'}</Tag>,
                                    it will be assigned to team <Tag size="sm" colorScheme="green" borderRadius="full">{teams.find(t => sameId(t.id, formData.assigned_team_id))?.name || 'N/A'}</Tag>.
                                </Text>
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onFormClose} size="sm" borderRadius="lg">Cancel</Button>
                        <Button colorScheme="blue" onClick={handleFormSubmit} isLoading={isLoading}
                            size="sm" borderRadius="lg" _hover={{ transform:'translateY(-1px)', boxShadow:'lg' }}
                            transition="all 0.2s">
                            Save Rule
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ═══════ DELETE DIALOG ═══════ */}
            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}
                motionPreset="slideInBottom">
                <AlertDialogOverlay backdropFilter="blur(4px)">
                    <AlertDialogContent borderRadius="xl">
                        <AlertDialogHeader fontSize="md" fontWeight="700">
                            <HStack spacing={2}><Icon as={MdWarning} color="red.500" /><Text>Delete Assignment Rule</Text></HStack>
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            Are you sure you want to delete "<Text as="span" fontWeight="700">{ruleToDelete?.rule_name}</Text>"?
                            This action cannot be undone and may affect work order assignments.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose} size="sm" borderRadius="lg">Cancel</Button>
                            <Button colorScheme="red" onClick={() => handleDeleteRule(ruleToDelete?.id)} ml={3}
                                size="sm" borderRadius="lg" leftIcon={<DeleteIcon />}>Delete</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* ═══════ BULK DELETE DIALOG ═══════ */}
            <AlertDialog isOpen={isBulkDeleteOpen} leastDestructiveRef={cancelRef} onClose={onBulkDeleteClose}
                motionPreset="slideInBottom">
                <AlertDialogOverlay backdropFilter="blur(4px)">
                    <AlertDialogContent borderRadius="xl">
                        <AlertDialogHeader fontSize="md" fontWeight="700">
                            <HStack spacing={2}><Icon as={MdWarning} color="red.500" /><Text>Delete Multiple Rules</Text></HStack>
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            Are you sure you want to delete <Text as="span" fontWeight="700">{selectedRules.length}</Text> rule(s)?
                            This action cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onBulkDeleteClose} size="sm" borderRadius="lg">Cancel</Button>
                            <Button colorScheme="red" onClick={handleBulkDelete} ml={3} isLoading={isLoading}
                                size="sm" borderRadius="lg" leftIcon={<DeleteIcon />}>Delete All</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default AssignmentRuleManagementComponent;